import { Link } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
// Plugins do not depend on `posthog-js` directly — the host wraps it
// through `@reflex/ui/client/posthog` and reuses the singleton initialised by the
// shell. This stays a no-op when PostHog is disabled (no key, or
// localhost).
import { posthog, isPostHogEnabled } from '@reflex/ui/client/posthog';
import { ModelPicker } from '@reflex/ui/components/agent/ModelPicker';
import type { ChatModel } from '@reflex/shared';
import { useOrgRoutes } from '@reflex/ui/client/context';
import { captureChatModelSwitched } from './chat-analytics';

interface ModelSelectorProps {
  model: ChatModel;
  onModelChange: (model: ChatModel) => void;
  enabledModelIds?: Set<string>;
  /**
   * Render the trigger as a compact composer-toolbar control. Uses the
   * shared `Button` (outline, `h-7`/`text-xs`) so it lines up pixel-for-pixel
   * with the sibling `ModelProviderSecretPicker` key picker in the chat
   * composer toolbar. Defaults to the larger chat-header trigger.
   */
  compact?: boolean;
  /**
   * Render the compact trigger with no fill or border (ghost button). Used
   * in the flow generate composer so the picker sits flush on the input.
   */
  ghost?: boolean;
  /** Optional className merged onto the trigger button. */
  className?: string;
}

export function ModelSelector({
  model,
  onModelChange,
  enabledModelIds,
  compact = false,
  ghost = false,
  className,
}: ModelSelectorProps) {
  const orgRoutes = useOrgRoutes();

  /**
   * Wraps `onModelChange` to fire a PostHog `chat_model_switched` event
   * whenever the user picks a different model. Same-model selections
   * (clicking the active model) are no-ops to avoid inflating switch
   * counts. The capture is best-effort — a posthog failure must never
   * block the model change.
   */
  const handleModelChange = (next: string | undefined) => {
    if (!next || next === model) return;
    const nextModel = next as ChatModel;
    captureChatModelSwitched(
      {
        capture: (event, props) => posthog.capture(event, props),
        isEnabled: isPostHogEnabled,
      },
      model,
      nextModel,
    );
    onModelChange(nextModel);
  };

  const triggerVariant = compact ? (ghost ? 'compact-ghost' : 'compact') : 'header';

  const manageModels = (
    <Link
      to={orgRoutes.orgModelProviders}
      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
    >
      <Settings2 className="h-3 w-3" />
      Manage models
    </Link>
  );

  const configureModels = (
    <Link
      to={orgRoutes.orgModelProviders}
      className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
    >
      <Settings2 className="h-3 w-3" />
      Configure models
    </Link>
  );

  return (
    <ModelPicker
      value={model}
      onChange={handleModelChange}
      enabledModelIds={enabledModelIds}
      triggerVariant={triggerVariant}
      allowInherit={false}
      allowCustom={false}
      searchPlaceholder="Search models…"
      footer={manageModels}
      emptyCta={configureModels}
      className={className}
      data-testid="model-selector"
    />
  );
}
